/**
 * Titanet logo with automatic fallback.
 *
 * The official files live in `public/brand/` (see `public/brand/README.md`). Each logo has a list of
 * candidate files: the first one that actually decodes as an image is used. If none is present
 * (the SPA fallback answers missing files with index.html, which fails to decode), an original
 * placeholder is drawn instead: a stylized "T" and a "TITANET" text wordmark.
 */
import { useEffect, useState } from 'react';

export const BRAND = {
  product: 'Titanet Voice',
  network: 'Titanet',
  domain: 'titanet.it',
  url: 'https://titanet.it',
} as const;

export const LOGO_FULL = ['/brand/logo.webp', '/brand/logo.png', '/brand/logo.svg'];
export const LOGO_MARK = ['/brand/logo-mark.png', '/brand/logo-mark.webp', '/brand/logo-mark.svg'];
export const FAVICON = ['/brand/favicon.png', '/brand/favicon.svg', '/brand/favicon.ico', ...LOGO_MARK];
const FAVICON_PLACEHOLDER = '/favicon.svg';

const probes = new Map<string, Promise<boolean>>();

/** Resolves true if `url` loads and decodes as an image (cached per URL). */
export function probeImage(url: string): Promise<boolean> {
  let p = probes.get(url);
  if (!p) {
    p = new Promise<boolean>((resolve) => {
      if (typeof Image === 'undefined') return resolve(false);
      const img = new Image();
      img.onload = () => resolve(img.naturalWidth > 0);
      img.onerror = () => resolve(false);
      img.src = url;
    });
    probes.set(url, p);
  }
  return p;
}

/** First candidate that loads, or null if none does. */
export async function firstAvailable(candidates: readonly string[]): Promise<string | null> {
  for (const url of candidates) if (await probeImage(url)) return url;
  return null;
}

const resolved = new Map<string, string | null>();

/** `undefined` while probing, then the first available URL or null. */
function useAsset(candidates: readonly string[]): string | null | undefined {
  const key = candidates.join('|');
  const [url, setUrl] = useState<string | null | undefined>(() => (resolved.has(key) ? resolved.get(key) : undefined));
  useEffect(() => {
    if (resolved.has(key)) return setUrl(resolved.get(key));
    let alive = true;
    void firstAvailable(candidates).then((u) => {
      resolved.set(key, u);
      if (alive) setUrl(u);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return url;
}

/** Points the page favicon (and apple-touch-icon) at the official icon, or at the placeholder. */
export async function applyBrandFavicon(): Promise<void> {
  const icon = (await firstAvailable(FAVICON)) ?? FAVICON_PLACEHOLDER;
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  if (link.getAttribute('href') !== icon) {
    link.type = icon.endsWith('.svg') ? 'image/svg+xml' : icon.endsWith('.ico') ? 'image/x-icon' : 'image/png';
    link.href = icon;
  }
  const touch = await firstAvailable(['/brand/apple-touch-icon.png', '/brand/logo-mark.png']);
  if (touch && !document.querySelector('link[rel="apple-touch-icon"]')) {
    const t = document.createElement('link');
    t.rel = 'apple-touch-icon';
    t.href = touch;
    document.head.appendChild(t);
  }
}

/** Original placeholder symbol: a stylized "T" with a lightning cut, on an azure tile. */
function MarkFallback({ size }: { size: number }) {
  return (
    <svg className="logo-mark-svg" width={size} height={size} viewBox="0 0 48 48" aria-hidden>
      <rect width="48" height="48" rx="11" className="lm-bg" />
      <path d="M0 34 L48 14 L48 22 L0 42 Z" className="lm-ray" />
      <path d="M10 10 H38 L35 18 H28 L22 40 L18 36 L21 18 H13 Z" className="lm-t" />
      <path d="M10 10 H38 L37.2 12.2 H11.2 Z" className="lm-t-hi" />
    </svg>
  );
}

export function Logo({ variant = 'full', size = 36, className = '' }: { variant?: 'full' | 'mark'; size?: number; className?: string }) {
  const url = useAsset(variant === 'full' ? LOGO_FULL : LOGO_MARK);
  const loading = url === undefined;

  if (variant === 'mark') {
    return (
      <span className={`logo logo-mark${className ? ` ${className}` : ''}`} style={{ width: size, height: size }} data-logo={url ? 'official' : 'fallback'}>
        {url ? <img src={url} width={size} height={size} alt="" draggable={false} decoding="async" /> : loading ? null : <MarkFallback size={size} />}
      </span>
    );
  }

  return (
    <span className={`logo logo-full${className ? ` ${className}` : ''}`} style={{ height: size }} data-logo={url ? 'official' : 'fallback'}>
      {url ? (
        <img src={url} height={size} alt={BRAND.network} draggable={false} decoding="async" />
      ) : loading ? (
        <span className="sr-only">{BRAND.network}</span>
      ) : (
        <>
          <Logo variant="mark" size={size} />
          <span className="logo-wordmark" style={{ fontSize: Math.round(size * 0.62) }} aria-label={BRAND.network}>
            TITANET
          </span>
        </>
      )}
    </span>
  );
}
